import { createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { axiosAuth, axiosUsers } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import { sanitizePatientRequestValues } from "../../functions/auth/mapSignupPayload";
import { clearSessionLogout } from "../../../auth/sessionLogout";


/* ============================================================
   ERROR NORMALIZATION
============================================================ */

export const toRejection = (
  error,
  fallbackMessage = "Request failed"
) => {
  // Error normalized by interceptor
  if (error?.status && error?.data) {
    return {
      statusCode: error.status,
      message: error.data?.message || fallbackMessage,
      error: error.data?.error,
    };
  }

  // Normal Axios error
  if (error?.response) {
    return {
      statusCode: error.response.status,
      message:
        error.response.data?.message ||
        fallbackMessage,
      error: error.response.data?.error,
    };
  }

  return {
    statusCode: 500,
    message: fallbackMessage,
    error: "Internal server error",
  };
};


/* ============================================================
   UTILS
============================================================ */

const normalizeEmail = (email) =>
  typeof email === "string"
    ? email.trim()
    : email;


/**
 * Supprime une éventuelle session de sous-profil.
 */
const clearActingSession = () => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem("actingSession");
  } catch {
    // Ignore storage access errors
  }
};


/**
 * Sauvegarde la session patient.
 */
const savePatientSession = ({
  accessToken,
  userId,
  user,
}) => {
  clearSessionLogout();
  if (!accessToken) return;

  clearActingSession();

  const isProductionDomain =
    typeof window !== "undefined" &&
    window.location.hostname === "front.mediwave.tn";
  const sessionCookieOptions = {
    path: "/",
    sameSite: isProductionDomain ? "None" : "Lax",
    ...(isProductionDomain
      ? { domain: ".mediwave.tn", secure: true }
      : {}),
  };

  // Token patient
  Cookies.remove("accessToken", { path: "/" });
  Cookies.remove("accessToken", { path: "/", domain: ".mediwave.tn" });
  Cookies.set("accessToken", accessToken, sessionCookieOptions);
  try {
    window.localStorage.setItem("patientAccessToken", accessToken);
    window.sessionStorage.setItem("patientAccessToken", accessToken);
  } catch {
    // Ignore storage access errors
  }

  // Détermine l'id utilisateur
  const id =
    user?.id ||
    user?._id ||
    userId ||
    null;

  if (id) {
    Cookies.set("userId", id, {
      path: "/",
      sameSite: "Lax",
    });

    Cookies.set("id", id, {
      path: "/",
      sameSite: "Lax",
    });
  }

  // Permet aussi à isDoctorSession() de reconnaître
  // explicitement qu'on est dans une session patient
  if (typeof window !== "undefined") {
    localStorage.setItem("userRole", "patient");
    // sessionStorage is isolated per browser tab (unlike localStorage,
    // which is shared across every tab of the same origin). A doctor and
    // patient logged in in separate tabs of the same browser would
    // otherwise overwrite each other's "userRole" and get misclassified.
    // This is the reliable, tab-scoped signal isDoctorSession() checks first.
    window.sessionStorage.setItem("activeSessionRole", "patient");
  }

  // Authorization par défaut pour axiosUsers
  axiosUsers.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${accessToken}`;
};


/**
 * Extraction sécurisée du payload JWT.
 */
const decodeJwtPayload = (token) => {
  try {
    if (!token) return null;

    const parts = token.split(".");

    if (parts.length !== 3) {
      return null;
    }

    let base64 = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    while (base64.length % 4) {
      base64 += "=";
    }

    return JSON.parse(atob(base64));
  } catch (error) {
    console.warn(
      "[decodeJwtPayload] Unable to decode token",
      error
    );

    return null;
  }
};


/* ============================================================
   GET CURRENT USER
============================================================ */

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",

  async (_, { rejectWithValue }) => {
    try {
      const token = Cookies.get("accessToken") || Cookies.get("auth_token") || Cookies.get("token") || null;

      // Un access token patient peut être conservé uniquement dans un cookie
      // httpOnly. Dans ce cas JavaScript ne le voit pas, mais Axios transmet le
      // cookie avec `withCredentials` et `/me` reste l'autorité de session.
      if (token) {
        const payload = decodeJwtPayload(token);

        if (String(payload?.role || "").toLowerCase() !== "patient") {
          return rejectWithValue("Patient session required");
        }
      }

      // Appel /users/me
      const res = await axiosUsers.get(
        ENDPOINTS.USERS.ME,
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        }
      );

      const user =
        res.data?.data ??
        res.data;

      const id =
        user?.id ||
        user?._id ||
        null;

      if (id) {
        Cookies.set("userId", id, {
          path: "/",
          sameSite: "Lax",
        });

        Cookies.set("id", id, {
          path: "/",
          sameSite: "Lax",
        });
      }

      return user;

    } catch (error) {
      console.error(
        "[getCurrentUser] Error:",
        error?.response?.data ||
        error?.message ||
        error
      );

      return rejectWithValue(
        toRejection(
          error,
          "Unable to get current user"
        )
      );
    }
  }
);


/* ============================================================
   LOGIN USER
============================================================ */

export const loginUser = createAsyncThunk(
  "auth/loginUser",

  async (
    { email, password },
    { rejectWithValue }
  ) => {
    try {
      clearActingSession();

      const response = await axiosAuth.post(
        ENDPOINTS.AUTH.LOGIN,
        {
          email: normalizeEmail(email),
          password,
        }
      );

      /*
       * Supporte les deux formats :
       *
       * {
       *   accessToken: ...
       * }
       *
       * ou
       *
       * {
       *   data: {
       *     accessToken: ...
       *   }
       * }
       */
      const data =
        response.data?.data ??
        response.data;

      console.log(
        "[loginUser] Login response:",
        data
      );


      /* =====================================================
         1. LOGIN TERMINÉ
         IMPORTANT :
         accessToken doit être vérifié AVANT userId.
      ====================================================== */

      if (data?.accessToken) {
        const accessToken = data.accessToken;

        savePatientSession({
          accessToken,
          userId: data?.userId,
          user: data?.user,
        });


        /*
         * Maintenant seulement on appelle /users/me.
         *
         * Le Bearer est envoyé explicitement même si
         * l'intercepteur Axios sait déjà le faire.
         */
        const meResponse = await axiosUsers.get(
          ENDPOINTS.USERS.ME,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const currentUser =
          meResponse.data?.data ??
          meResponse.data;

        const id =
          currentUser?.id ||
          currentUser?._id ||
          data?.userId ||
          null;

        if (id) {
          Cookies.set("userId", id, {
            path: "/",
            sameSite: "Lax",
          });

          Cookies.set("id", id, {
            path: "/",
            sameSite: "Lax",
          });
        }

        return {
          ...data,
          accessToken,
          user: currentUser,
        };
      }


      /* =====================================================
         2. LOGIN NÉCESSITE OTP / 2FA
      ====================================================== */

      if (
        data?.next === "VERIFY_OTP" ||
        data?.next === "VERIFY_2FA" ||
        data?.tempToken
      ) {
        return {
          ...data,
          requiresTwoFactor: true,
        };
      }


      /* =====================================================
         3. Ancien format éventuel du backend
         userId seul ne doit PAS automatiquement signifier
         login terminé.
      ====================================================== */

      if (data?.userId && !data?.accessToken) {
        return {
          ...data,
          requiresTwoFactor: true,
        };
      }


      /* =====================================================
         4. Réponse inattendue
      ====================================================== */

      console.warn(
        "[loginUser] Unexpected login response:",
        data
      );

      return data;

    } catch (error) {
      console.error(
        "[loginUser] Error:",
        error?.response?.data ||
        error?.message ||
        error
      );

      return rejectWithValue(
        toRejection(
          error,
          "Login failed"
        )
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async ({ token: tokenOverride } = {}, { rejectWithValue }) => {
    const token =
      tokenOverride ||
      Cookies.get("accessToken") ||
      Cookies.get("auth_token") ||
      Cookies.get("token") ||
      (typeof window !== "undefined" &&
        (window.localStorage.getItem("patientAccessToken") ||
          window.sessionStorage.getItem("patientAccessToken")));

    const clearPatientSession = () => {
      if (typeof window !== "undefined") {
        localStorage.removeItem("patientAccessToken");
        sessionStorage.removeItem("patientAccessToken");
        if (sessionStorage.getItem("activeSessionRole") === "patient") {
          sessionStorage.removeItem("activeSessionRole");
        }
        localStorage.removeItem("userRole");
      }

      ["accessToken", "auth_token", "token", "authToken", "jwt", "userId", "id"].forEach((key) => {
        Cookies.remove(key, { path: "/" });
        Cookies.remove(key, { path: "/", domain: ".mediwave.tn" });
      });
    };

    try {
      const request = axiosAuth.post(
        ENDPOINTS.AUTH.LOGOUT,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      clearPatientSession();
      await request;
      return { success: true };
    } catch (error) {
      clearPatientSession();
      if ([401, 403].includes(error?.response?.status)) {
        return { success: true };
      }
      return rejectWithValue(toRejection(error, "Logout failed"));
    }
  },
);


/* ============================================================
   VERIFY 2FA
============================================================ */

const getStoredPatientTwoFactorUserId = () => {
  if (typeof window === "undefined") return "";
  try {
    const challenge = JSON.parse(
      window.sessionStorage.getItem("patientTwoFactorChallenge") || "null",
    );
    return String(challenge?.userId || "").trim();
  } catch {
    return "";
  }
};

export const verifyTwoFA = createAsyncThunk(
  "auth/verifyTwoFA",

  async (
    payload,
    { rejectWithValue }
  ) => {
    try {
      const userId = String(
        payload?.userId || getStoredPatientTwoFactorUserId(),
      ).trim();
      const code = String(payload?.code || "").replace(/\D/g, "");
      if (!payload?.tempToken && (!userId || code.length !== 6)) {
        return rejectWithValue({
          statusCode: 400,
          message: "2FA user or code is missing",
        });
      }
      const body = payload?.tempToken
        ? {
            tempToken: payload.tempToken,
            code,
          }
        : {
            userId,
            code,
          };


      const response = await axiosAuth.post(
        ENDPOINTS.AUTH.VERIFY_2FA,
        body,
        { allowedRequestFields: ["userId"] }
      );


      const data =
        response.data?.data ??
        response.data;


      console.log(
        "[verifyTwoFA] Response:",
        data
      );


      const accessToken =
        data?.accessToken;

      /* =====================================================
         Sauvegarde immédiatement le token AVANT /users/me
      ====================================================== */

      if (accessToken) {
        savePatientSession({
          accessToken,
          userId:
            data?.userId ||
            userId,
          user: data?.user,
        });
      }


      /* =====================================================
         Récupération du patient authentifié
      ====================================================== */

      const meResponse = await axiosUsers.get(
        ENDPOINTS.USERS.ME,
        {
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : undefined,
        }
      );


      const currentUser =
        meResponse.data?.data ??
        meResponse.data;


      const id =
        currentUser?.id ||
        currentUser?._id ||
        data?.userId ||
        payload?.userId ||
        null;


      if (id) {
        Cookies.set("userId", id, {
          path: "/",
          sameSite: "Lax",
        });

        Cookies.set("id", id, {
          path: "/",
          sameSite: "Lax",
        });
      }


      return {
        step: "done",
        ...data,
        user: currentUser,
        accessToken,
      };

    } catch (error) {
      console.error(
        "[verifyTwoFA] Error:",
        error?.response?.data ||
        error?.message ||
        error
      );

      return rejectWithValue(
        toRejection(
          error,
          "Verification failed"
        )
      );
    }
  }
);


/* ============================================================
   REGISTER USER
============================================================ */

export const registerUser = createAsyncThunk(
  "auth/registerUser",

  async (
    userData,
    { rejectWithValue }
  ) => {
    try {
      const payload = sanitizePatientRequestValues({
        ...userData,
      });

      if (payload.email) {
        payload.email =
          normalizeEmail(payload.email);
      }

      const { data } =
        await axiosUsers.post(
          ENDPOINTS.USERS.REGISTER,
          payload
        );


      return data?.data ?? data;

    } catch (error) {
      return rejectWithValue(
        toRejection(
          error,
          "Registration failed"
        )
      );
    }
  }
);


/* ============================================================
   REQUEST PASSWORD RESET
============================================================ */

export const requestPasswordReset =
  createAsyncThunk(
    "auth/requestPasswordReset",

    async (
      email,
      { rejectWithValue }
    ) => {
      try {
        const { data } =
          await axiosAuth.post(
            ENDPOINTS.AUTH.REQUEST_RESET,
            {
              email:
                normalizeEmail(email),
            }
          );


        return data?.data ?? data;

      } catch (error) {
        return rejectWithValue(
          toRejection(
            error,
            "Failed to request password reset"
          )
        );
      }
    }
  );


/* ============================================================
   VERIFY RESET CODE
============================================================ */

export const verifyResetCode =
  createAsyncThunk(
    "auth/verifyResetCode",

    async (
      {
        email,
        resetCode,
      },
      { rejectWithValue }
    ) => {
      try {
        const payload = {
          email:
            normalizeEmail(email),

          resetCode: String(
            Array.isArray(resetCode)
              ? resetCode.join("")
              : resetCode
          ),
        };


        const { data } =
          await axiosAuth.post(
            ENDPOINTS.AUTH.VERIFY_RESET_CODE,
            payload
          );

        const result = data?.data ?? data;
        if (result?.valid === false) {
          return rejectWithValue({
            statusCode: 400,
            message: result.message || "Invalid or expired code",
          });
        }

        return result;

      } catch (error) {
        return rejectWithValue(
          toRejection(
            error,
            "Invalid or expired code"
          )
        );
      }
    }
  );


/* ============================================================
   RESET PASSWORD
============================================================ */

export const resetPassword =
  createAsyncThunk(
    "auth/resetPassword",

    async (
      {
        email,
        resetCode,
        newPassword,
        confirmPassword,
      },
      { rejectWithValue }
    ) => {
      try {
        const { data } =
          await axiosAuth.post(
            ENDPOINTS.AUTH.RESET_PASSWORD,
            {
              email:
                normalizeEmail(email),

              resetCode:
                Array.isArray(resetCode)
                  ? resetCode.join("")
                  : String(resetCode),

              newPassword,
              confirmPassword,
            }
          );


        return data?.data ?? data;

      } catch (error) {
        return rejectWithValue(
          toRejection(
            error,
            "Failed to reset password"
          )
        );
      }
    }
  );


/* ============================================================
   RESEND TWO FACTOR CODE
============================================================ */

export const resendTwoFactorCode =
  createAsyncThunk(
    "auth/resendTwoFactorCode",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const payloadUserId =
          typeof payload === "string" ? payload : payload?.userId;
        const userId = String(
          payloadUserId || getStoredPatientTwoFactorUserId(),
        ).trim();
        if (!userId) {
          return rejectWithValue({
            statusCode: 400,
            message: "2FA user is missing",
          });
        }

        const body = { userId };


        const { data } =
          await axiosAuth.post(
            ENDPOINTS.AUTH.RESEND_2FA,
            body,
            { allowedRequestFields: ["userId"] }
          );


        return data?.data ?? data;

      } catch (error) {
        return rejectWithValue(
          toRejection(
            error,
            "Failed to resend 2FA code"
          )
        );
      }
    }
  );
